import { useCallback, useEffect, useState } from 'react';
import './App.css';
import AuthPage from './Views/AuthPage';
import BookmarksPage from './Views/BookmarksPage';
import AdminPanel from './Views/AdminPanel';
import ApprovalPage from './Views/ApprovalPage';
import { WebAuthnProvider, useWebAuthn } from './contexts/WebAuthnContext';
import { CategoryProvider } from './contexts/CategoryContext';
import { PageProvider } from './contexts/PageContext';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import { Snackbar, Alert, CircularProgress, Box } from '@mui/material';
import withLayout from './utils/withLayout';
import PageRoute from './Components/PageRoute';
import { DebugConsole } from './Components/DebugConsole';
import UpdatesBanner from './Components/UpdatesBanner';
import SessionExpiryBanner from './Components/SessionExpiryBanner';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import CssBaseline from '@mui/material/CssBaseline';

function AppContent() {
  const { user, initializing, snackbar, setSnackbar } = useWebAuthn();
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get('debuginfo') === 'inline';
  const [swRegistration, setSwRegistration] =
    useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const { registration } = (event as CustomEvent).detail;
      setSwRegistration(registration);
    };
    document.addEventListener('sw-update-available', handler);
    return () => document.removeEventListener('sw-update-available', handler);
  }, []);

  const handleUpdateRefresh = useCallback(() => {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Reload once the new SW takes over
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }
  }, [swRegistration]);

  // Gate ONLY on the initial session-validation (page-load) flag. Gating on the
  // broader `loading` flag would unmount <AuthPage/> during a sign-in ceremony
  // (register/authenticate both toggle `loading`), discarding useSmartAuth's
  // `view` and dropping the post-NotAllowedError escape-hatch back to a bare
  // form. `initializing` flips false exactly once and never again.
  if (initializing)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        Loading...
        <CircularProgress />
      </Box>
    );

  return (
    <CategoryProvider>
      <CssBaseline />
      <Routes>
        <Route
          path="/admin"
          element={
            <PageRoute title="Admin">
              {user ? withLayout(AdminPanel)() : <Navigate to="/" replace />}
            </PageRoute>
          }
        />
        <Route
          path="/admin/approve"
          element={
            <PageRoute title="Approve Invitation">
              <ApprovalPage />
            </PageRoute>
          }
        />
        <Route
          path="/*"
          element={
            <PageRoute title="Bookmarks">
              {user ? (
                withLayout(BookmarksPage)()
              ) : (
                <Box
                  sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box sx={{ width: '100%', maxWidth: 480 }}>
                    <AuthPage />
                  </Box>
                </Box>
              )}
            </PageRoute>
          }
        />
      </Routes>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {showDebug && <DebugConsole />}

      <UpdatesBanner
        updatesAvailable={swRegistration !== null}
        onAcknowledge={handleUpdateRefresh}
      />

      {user && <SessionExpiryBanner />}
    </CategoryProvider>
  );
}

function App() {
  return (
    <Router>
      <PageProvider>
        <WebAuthnProvider>
          <AppContent />
        </WebAuthnProvider>
      </PageProvider>
    </Router>
  );
}

export default App;
